import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticleLocale } from "../../hooks/useArticleLocale";
import { formatDate } from "../../utils/blogSeo";
import "./BlogCard.css";

/**
 * BlogCard — presentational component.
 *
 * Renders a single article preview. The `featured` variant stretches
 * to full grid width with a side-by-side layout.
 *
 * No business logic — pure display.
 */
export default function BlogCard({ article, featured }) {
  const { t } = useTranslation("blog");
  const { title, description } = useArticleLocale(article);
  const url = `/blog/${article.slug}`;

  return (
    <article
      className={`blog-card${featured ? " blog-card--featured" : ""}`}
      aria-label={article.title}
    >
      <Link to={url} className="blog-card-img-wrap" tabIndex={-1} aria-hidden="true">
        <img
          src={article.featuredImage}
          alt={article.featuredImageAlt}
          className="blog-card-img"
          loading="lazy"
          width="800"
          height="500"
        />
        <span className="blog-card-category">{article.category}</span>
      </Link>

      <div className="blog-card-body">
        <div className="blog-card-meta" aria-label="Article metadata">
          <time dateTime={article.date}>{formatDate(article.date)}</time>
          <span className="blog-card-dot" aria-hidden="true" />
          <span aria-label={`${article.readingTime} minute read`}>
            {article.readingTime} min read
          </span>
        </div>

        <h2 className="blog-card-title">
          <Link to={url}>{title}</Link>
        </h2>

        <p className="blog-card-desc">{description}</p>

        <div className="blog-card-footer">
          <div className="blog-card-author">
            {article.author?.photo && (
              <img
                src={article.author.photo}
                alt={article.author?.name ?? ""}
                className="blog-card-author-img"
                loading="lazy"
                width="28"
                height="28"
              />
            )}
            <span>{article.author?.name}</span>
          </div>
          <Link
            to={url}
            className="blog-card-read"
            aria-label={`${t("card.read")}: ${title}`}
          >
            {t("card.read")} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </article>
  );
}

// ─── PropTypes ────────────────────────────────────────────────────────────────

const authorShape = PropTypes.shape({
  name:  PropTypes.string.isRequired,
  photo: PropTypes.string,   // nullable — Sanity articles may not have a team photo
  role:  PropTypes.string.isRequired,
});

export const articleShape = PropTypes.shape({
  slug:             PropTypes.string.isRequired,
  title:            PropTypes.string.isRequired,
  description:      PropTypes.string.isRequired,
  date:             PropTypes.string.isRequired,
  updatedAt:        PropTypes.string,
  author:           authorShape.isRequired,
  category:         PropTypes.string.isRequired,
  tags:             PropTypes.arrayOf(PropTypes.string).isRequired,
  keywords:         PropTypes.arrayOf(PropTypes.string),
  featuredImage:    PropTypes.string.isRequired,
  featuredImageAlt: PropTypes.string.isRequired,
  featured:         PropTypes.bool.isRequired,
  readingTime:      PropTypes.number.isRequired,
});

BlogCard.propTypes = {
  article:  articleShape.isRequired,
  featured: PropTypes.bool,
};

BlogCard.defaultProps = {
  featured: false,
};
